import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import CustomInput from "../common/CustomInput";
import CustomButton from "../common/CustomButton";
import { useSelector } from "react-redux";
import { callApi } from "../common/Apicall";
import imageCompression from "browser-image-compression";
import { FaCamera } from "react-icons/fa";

const API_URL = process.env.REACT_APP_URL;

const UserProfileBody = ({ onClose, profileImage }) => {
  const [isLoading, setIsLoading] = useState(false);
  const currentUserId = useSelector(
    (state) => state?.data?.userInfo?.data?.data?.UserId
  );
  const { data } = useSelector((state) => state.data.userInfo.data);

  const fileInputRef = useRef(null);
  const [previewImage, setPreviewImage] = useState(null);

  const [errors, setErrors] = useState({});
  const [userModel, setUserModel] = useState({
    firstName: "",
    lastName: "",
    empId: "",
    email: "",
    password: "",
    teamId: "",
    createdBy: data.Email,
    status: "",
    isActive: true,
    confirmPassword: "",
    profilePicture: "",
  });

  const [initialData, setInitialData] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [profileChanged, setProfileChanged] = useState(false);

  /** =========================
   * Validation Logic
   * ========================= */
  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "firstName":
        if (!value?.trim()) error = "First Name is required";
        break;
      case "lastName":
        if (!value?.trim()) error = "Last Name is required";
        break;
      case "password":
        if (!value?.trim()) error = "Password is required";
        break;
      case "confirmPassword":
        if (!value?.trim()) error = "Confirm Password is required";
        else if (value !== userModel.password) error = "Passwords do not match";
        break;
      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [name]: error }));
    return error === "";
  };

  // Validate all required fields on save
  const validateForm = () => {
    const fieldsToValidate = [
      "firstName",
      "lastName",
      "password",
      "confirmPassword",
    ];
    let isValid = true;

    fieldsToValidate.forEach((field) => {
      if (!validateField(field, userModel[field])) {
        isValid = false;
      }
    });

    return isValid;
  };

  /** =========================
   * Image Upload
   * ========================= */
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];

    if (file) {
      if (file.size > 1024 * 1024) {
        toast.error("File size must be less than 1MB!");
        return;
      }

      try {
        const options = {
          maxSizeMB: 0.02,
          maxWidthOrHeight: 300,
          useWebWorker: true,
        };

        const compressedFile = await imageCompression(file, options);

        if (compressedFile.size > 20 * 1024) {
          toast.error("Unable to compress below 20KB. Try a smaller image.");
          return;
        }

        const compressedImageUrl = URL.createObjectURL(compressedFile);

        setPreviewImage(compressedImageUrl);
        setUserModel((prevModel) => ({
          ...prevModel,
          profilePicture: compressedFile,
        }));
        setProfileChanged(true);
      } catch (error) {
        console.error("Image compression error:", error);
        toast.error("Failed to compress image!");
      }
    }
  };

  /** =========================
   * Save Button
   * ========================= */
  const handleSave = () => {
    const isValid = validateForm();

    if (!isValid) {
      toast.error("Please fill in all required fields before saving!");
      return;
    }

    if (userModel.password !== userModel.confirmPassword) {
      toast.error("Password and Confirm Password do not match!");
      return;
    }

    const payload = { ...userModel };

    // Remove profilePicture if it hasn't changed
    if (!profileChanged) {
      delete payload.profilePicture;
    }

    const { isUserType, ...finalPayload } = payload;
    !finalPayload.updatedAt && delete finalPayload.updatedAt;

    callApi(API_URL + "/Users/SaveUser", "POST", finalPayload, true)
      .then((res) => {
        if (profileChanged) {
          localStorage.setItem("profilePicture", res?.data?.ProfilePicture);
        }
        localStorage.setItem(
          "fullName",
          payload.firstName + " " + payload?.lastName
        );
        onClose();
        toast.success("User updated successfully!");
      })
      .catch((err) => {
        toast.error(err.message || "Error occurred while saving user!");
      });
  };

  /** =========================
   * Input Change
   * ========================= */
  const handleAvatarClick = () => {
    fileInputRef.current.click();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserModel({
      ...userModel,
      [name]: value,
    });

    // Validate field as user types
    validateField(name, value);
  };

  /** =========================
   * Load Initial User Data
   * ========================= */
  useEffect(() => {
    if (currentUserId) {
      if (profileImage !== "null") {
        setPreviewImage(`data:image/png;base64,${profileImage}`);
      } else {
        setPreviewImage(null);
      }

      setIsLoading(true);
      callApi(API_URL + `/Users/GetUserById/${currentUserId}`, "GET")
        .then((userRes) => {
          const userData = {
            ...userRes,
            confirmPassword: userRes.password,
          };
          setUserModel(userData);
          setInitialData(userData);
          setIsLoading(false);
        })
        .catch((err) => {
          toast.error(err);
        });
    }
  }, [currentUserId, profileImage]);

  /** =========================
   * Detect Form Changes
   * ========================= */
  useEffect(() => {
    if (initialData && userModel) {
      const changed =
        Object.keys(userModel).some((key) => {
          if (key === "profilePicture") return false;
          return userModel[key] !== initialData[key];
        }) || profileChanged;

      setIsDirty(changed);
    }
  }, [userModel, profileChanged, initialData]);

  /** =========================
   * Render UI
   * ========================= */
  return (
    <div>
      {isLoading ? (
        <div className="flex w-[500px] justify-center items-center min-h-[25vh]">
          <div className="animate-spin border-4 border-blue-500 border-t-transparent w-8 h-8 rounded-full"></div>
        </div>
      ) : (
        <>
          {/* Avatar Upload */}
          <div className="flex justify-center mb-4 mt-2">
            <div className="relative">
              <div
                className="group relative w-32 h-32 rounded-full cursor-pointer"
                onClick={handleAvatarClick}
              >
                <img
                  src={
                    previewImage ||
                    "https://cdn-icons-png.flaticon.com/512/149/149071.png"
                  }
                  alt="Profile"
                  className="w-full h-full rounded-full object-cover border-2 border-gray-300"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <FaCamera className="h-8 w-8 text-white" />
                </div>
              </div>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                className="hidden"
                onChange={handleImageUpload}
              />
            </div>
          </div>

          {/* Form Fields */}
          <div className="grid gap-3 p-2 sm:grid-cols-2">
            {[
              { name: "firstName", label: "First Name", required: true },
              { name: "lastName", label: "Last Name", required: true },
              { name: "empId", label: "Employee ID", disabled: true },
              {
                name: "email",
                label: "Email",
                required: true,
                disabled: true,
              },
              {
                name: "password",
                label: "Password",
                required: true,
                type: "password",
              },
              {
                name: "confirmPassword",
                label: "Confirm Password",
                required: true,
                type: "password",
              },
            ].map((field) => (
              <div key={field.name} className="relative">
                <CustomInput
                  name={field.name}
                  label={field.label}
                  value={userModel[field.name] || ""}
                  type={field.type ? field.type : "text"}
                  onChange={handleInputChange}
                  required={field.required}
                  maxLength={30}
                  disabled={field.disabled}
                />
                {/* Show error message below input */}
                {errors[field.name] && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors[field.name]}
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="bg-lightGray mt-auto p-3 flex justify-end space-x-4">
            <CustomButton
              label="Save"
              onClick={handleSave}
              disable={!isDirty} // Disable only when there are no changes
            />
            <CustomButton
              label="Cancel"
              onClick={onClose}
              buttonType="cancel"
            />
          </div>
        </>
      )}
    </div>
  );
};

export default UserProfileBody;
